import React from 'react';
import classNames from 'classnames';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {useIsStickyButton} from '../../../providers/renderContext';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {AddToCartState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/AddToCartService/constants';
import {Button, ButtonSize, HoverStyles} from '@wix/wix-ui-tpa/cssVars';
import s from './BuyNowButton.scss';
import {classes as buyNowButtonStyles} from './BuyNowButton.st.css';

export interface BuyNowButtonProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  productInStock: boolean;
  isStickyButton: boolean;
}

export enum BuyNowButtonDataHook {
  Root = 'buy-now-button',
}

class BuyNowButtonComponent extends React.Component<BuyNowButtonProps> {
  private getCaption(
    t: Function,
    disabled: boolean,
    price: number,
    productInStock: boolean,
    addToCartState: AddToCartState,
    buyNowLabelOverride: string | null
  ): string {
    const isOutOfStockText = price > 0 || !productInStock;

    if (buyNowLabelOverride) {
      return buyNowLabelOverride;
    }

    if (addToCartState === AddToCartState.UNAVAILABLE) {
      return t('productPage.unavailable.button');
    }

    if (disabled) {
      return isOutOfStockText ? t('PRODUCT_OUT_OF_STOCK_BUTTON') : t('ADD_TO_CART_BUTTON_PRICE_ZERO');
    }

    if (addToCartState === AddToCartState.PRE_ORDER) {
      return t('productPage.preOrderNow.button');
    }

    return t('BUY_NOW_BUTTON');
  }

  public render(): JSX.Element {
    const {
      t,
      productInStock,
      isStickyButton,
      globals: {accessibilityEnabled, addToCartState, handleBuyNow, product, selectedVariant, labelOverrides},
    } = this.props;

    const {price} = selectedVariant || product;
    const disabled = addToCartState !== AddToCartState.ENABLED && addToCartState !== AddToCartState.PRE_ORDER;
    const caption = this.getCaption(t, disabled, price, productInStock, addToCartState, labelOverrides.cta.buyNow);

    const buttonClasses = classNames(buyNowButtonStyles.buyNowButton, s.buyNowButton);

    return (
      <Button
        data-wix-hideable={true}
        hoverStyle={HoverStyles.underline}
        type="button"
        onClick={() => handleBuyNow(accessibilityEnabled, isStickyButton)}
        disabled={disabled}
        data-hook={BuyNowButtonDataHook.Root}
        className={buttonClasses}
        size={ButtonSize.large}>
        {caption}
      </Button>
    );
  }
}

const BuyNowButtonWithIsStickyButton: React.FC<Omit<BuyNowButtonProps, 'isStickyButton'>> = (props) => {
  const isStickyButton = useIsStickyButton();
  return <BuyNowButtonComponent {...(props as BuyNowButtonProps)} isStickyButton={isStickyButton} />;
};

export const BuyNowButton = withGlobalProps(withTranslations('globals.texts')(BuyNowButtonWithIsStickyButton));
